import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { socialMediaApi } from '../lib/api';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { Link } from 'react-router-dom';
import {
  MegaphoneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  ArrowPathIcon,
  PlusIcon,
  ShieldCheckIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { useTeamId } from '../hooks/useTeamId';

const PLATFORMS = [
  { value: 'twitter', label: 'X (Twitter)', limit: 280 },
  { value: 'facebook', label: 'Facebook', limit: 63206 },
  { value: 'instagram', label: 'Instagram', limit: 2200 },
  { value: 'linkedin', label: 'LinkedIn', limit: 3000 },
  { value: 'youtube', label: 'YouTube', limit: 5000 },
  { value: 'tiktok', label: 'TikTok', limit: 2200 },
  { value: 'other', label: 'Other', limit: 0 },
];

const PRODUCT_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'heloc', label: 'HELOC' },
  { value: 'deposit', label: 'Deposit / Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'cd', label: 'Certificate of Deposit' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'commercial', label: 'Commercial Lending' },
  { value: 'other', label: 'Other' },
];

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; icon: any; label: string }> = {
    PENDING: { bg: 'bg-gray-100', text: 'text-gray-700', icon: ClockIcon, label: 'Pending' },
    PROCESSING: { bg: 'bg-blue-100', text: 'text-blue-700', icon: ArrowPathIcon, label: 'Analyzing' },
    PASSED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon, label: 'Passed' },
    FAILED: { bg: 'bg-red-100', text: 'text-red-700', icon: ExclamationTriangleIcon, label: 'Flagged' },
    ERROR: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon, label: 'Error' },
  };

  const c = config[status] || config.PENDING;
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'PROCESSING' || status === 'PENDING' ? 'animate-spin' : ''}`} />
      {c.label}
    </span>
  );
}

function PlatformLabel({ platform }: { platform: string }) {
  const p = PLATFORMS.find((p) => p.value === platform);
  return <span className="text-sm text-gray-700">{p?.label || 'Other'}</span>;
}

export default function SocialMediaPage() {
  const { hasFeature } = useFeatureAccess();
  const hasAccess = hasFeature('social_media_checker');
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [platform, setPlatform] = useState('twitter');
  const [productType, setProductType] = useState('general');
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);

  // Get user's team
  const { teamId } = useTeamId();

  // Stats query
  const { data: stats } = useQuery({
    queryKey: ['social-media-stats', teamId],
    queryFn: async () => (await socialMediaApi.getStats({ teamId })).data,
    enabled: hasAccess && !!teamId,
  });

  // List query with polling
  const { data: listData, isLoading } = useQuery({
    queryKey: ['social-media-checks', teamId],
    queryFn: async () => (await socialMediaApi.listChecks({ limit: 50, teamId })).data,
    enabled: hasAccess && !!teamId,
    refetchInterval: (query) => {
      const checks = query.state.data?.checks || [];
      const hasPending = checks.some((c: any) => c.status === 'PENDING' || c.status === 'PROCESSING');
      return hasPending ? 5000 : false;
    },
  });

  // Detail query for expanded check
  const { data: checkDetail } = useQuery({
    queryKey: ['social-media-check', expandedCheck],
    queryFn: async () => (await socialMediaApi.getCheck(expandedCheck!)).data,
    enabled: !!expandedCheck,
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: (data: { text: string; platform: string; productType: string }) =>
      socialMediaApi.checkPost({ ...data, teamId }),
    onSuccess: () => {
      toast.success('Social media post submitted for compliance review');
      setText('');
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['social-media-checks'] });
      queryClient.invalidateQueries({ queryKey: ['social-media-stats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to submit compliance check');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    submitMutation.mutate({ text: text.trim(), platform, productType });
  };

  // Character limit
  const platformConfig = PLATFORMS.find((p) => p.value === platform);
  const charLimit = platformConfig?.limit || 0;
  const charCount = text.length;
  const charPct = charLimit > 0 ? (charCount / charLimit) * 100 : 0;
  const charColor = charPct > 100 ? 'text-red-600' : charPct > 90 ? 'text-amber-600' : 'text-gray-500';

  // Upgrade prompt
  if (!hasAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <MegaphoneIcon className="mx-auto h-16 w-16 text-gray-300" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Social Media Compliance</h2>
          <p className="mt-2 text-gray-600">
            Check your social media posts for FDIC, NCUA, Reg Z, UDAAP, and Equal Housing compliance before publishing.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            This feature is available on Professional and Enterprise plans.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-block rounded-lg bg-teal-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    );
  }

  const checks = listData?.checks || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Compliance</h1>
          <p className="text-sm text-gray-600 mt-1">
            Check social media posts for regulatory compliance before publishing
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Check
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-50 rounded-lg">
              <MegaphoneIcon className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Checks</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalChecks ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Flagged</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.flaggedCount ?? 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <ShieldCheckIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Compliance Rate</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.complianceRate ?? 100}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* New Check Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Check Social Media Post</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Platform & Product Type Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  {PLATFORMS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                <select
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                >
                  {PRODUCT_TYPES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Post Content</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                placeholder="Paste your social media post content here..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 resize-none"
              />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-400">
                  Checks for FDIC/NCUA disclosure, Reg Z trigger terms, UDAAP, Equal Housing, and CAN-SPAM compliance
                </p>
                <p className={`text-xs font-medium ${charColor}`}>
                  {charCount.toLocaleString()}
                  {charLimit > 0 && ` / ${charLimit.toLocaleString()}`}
                  {' chars'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowForm(false); setText(''); }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!text.trim() || submitMutation.isPending}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitMutation.isPending ? 'Submitting...' : 'Check Compliance'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* History Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Checks</h2>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-300 animate-spin" />
            <p className="mt-2 text-sm text-gray-500">Loading checks...</p>
          </div>
        ) : checks.length === 0 ? (
          <div className="p-12 text-center">
            <MegaphoneIcon className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No social media compliance checks yet.</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Check Your First Post
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {checks.map((check: any) => (
              <div key={check.id}>
                <button
                  onClick={() => setExpandedCheck(expandedCheck === check.id ? null : check.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <StatusBadge status={check.status} />
                    <PlatformLabel platform={check.platform} />
                    <span className="text-xs text-gray-400 capitalize">
                      {check.productType?.replace('_', ' ')}
                    </span>
                    {check.violations > 0 && (
                      <span className="text-xs font-medium text-red-600">
                        {check.violations} violation{check.violations !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">
                      {formatDistanceToNow(new Date(check.createdAt), { addSuffix: true })}
                    </span>
                    {expandedCheck === check.id ? (
                      <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Detail */}
                {expandedCheck === check.id && checkDetail && (
                  <div className="px-6 pb-4 space-y-4 bg-gray-50 border-t border-gray-100">
                    {/* Extracted Text */}
                    {checkDetail.extractedText && (
                      <div className="mt-4">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Post Content</h4>
                        <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200 whitespace-pre-wrap">
                          {checkDetail.extractedText}
                        </p>
                      </div>
                    )}

                    {/* Violations */}
                    {checkDetail.violations?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Violations</h4>
                        <div className="space-y-2">
                          {checkDetail.violations.map((v: any, i: number) => (
                            <div key={i} className="bg-white rounded-lg border border-red-100 p-3">
                              <div className="flex items-start gap-2">
                                <ExclamationCircleIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                                  v.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                                }`} />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900">
                                    {v.ruleName || v.regulation || 'Compliance Flag'}
                                  </p>
                                  <p className="text-xs text-gray-600 mt-0.5">{v.reason || v.message}</p>
                                  {v.requiredAction && (
                                    <p className="text-xs text-teal-700 mt-1 font-medium">
                                      Required: {v.requiredAction}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* UDAAP Scores */}
                    {(checkDetail.udaapUnfairScore > 0 || checkDetail.udaapDeceptiveScore > 0 || checkDetail.udaapAbusiveScore > 0) && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">UDAAP Risk Scores</h4>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { label: 'Unfair', score: checkDetail.udaapUnfairScore },
                            { label: 'Deceptive', score: checkDetail.udaapDeceptiveScore },
                            { label: 'Abusive', score: checkDetail.udaapAbusiveScore },
                          ].map((u) => (
                            <div key={u.label} className="bg-white rounded-lg border border-gray-200 p-2 text-center">
                              <p className="text-xs text-gray-500">{u.label}</p>
                              <p className={`text-lg font-bold ${
                                (u.score || 0) >= 70 ? 'text-red-600' : (u.score || 0) >= 40 ? 'text-amber-600' : 'text-green-600'
                              }`}>
                                {u.score || 0}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Enforcement Context */}
                    {checkDetail.enforcementContext?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                          Related Enforcement Actions
                        </h4>
                        <div className="space-y-2">
                          {checkDetail.enforcementContext.map((ea: any, i: number) => (
                            <div key={i} className="bg-amber-50 rounded-lg border border-amber-200 p-3">
                              <div className="flex items-start gap-2">
                                <ExclamationTriangleIcon className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-amber-900">
                                    {ea.agency} — {ea.institutionName}
                                    {ea.penaltyAmount && ` ($${Number(ea.penaltyAmount).toLocaleString()} penalty)`}
                                  </p>
                                  <p className="text-xs text-amber-700 mt-0.5">
                                    {ea.summary?.slice(0, 200)}{ea.summary?.length > 200 ? '...' : ''}
                                  </p>
                                  <p className="text-xs text-amber-500 mt-1">
                                    {new Date(ea.actionDate).toLocaleDateString()} — {ea.violationTypes?.join(', ')}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggestions */}
                    {checkDetail.suggestions && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">AI Suggestions</h4>
                        <p className="text-sm text-gray-700 bg-white rounded-lg p-3 border border-gray-200 whitespace-pre-wrap">
                          {typeof checkDetail.suggestions === 'string'
                            ? checkDetail.suggestions
                            : JSON.stringify(checkDetail.suggestions, null, 2)}
                        </p>
                      </div>
                    )}

                    {/* Clean result */}
                    {check.status === 'PASSED' && checkDetail.violations?.length === 0 && (
                      <div className="flex items-center gap-2 text-green-700 bg-green-50 rounded-lg p-3 border border-green-200">
                        <CheckCircleIcon className="h-5 w-5" />
                        <p className="text-sm font-medium">No compliance issues detected. Post is ready for human review before publishing.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
